import React, { FC, useEffect, useRef, useState } from 'react';
import { Button, Input, Upload } from 'antd';
import { UploadChangeParam, UploadFile } from 'antd/es/upload/interface';
import { EFileStatus } from 'common/const/file.enum';
import { onInputKeyDown } from 'common/helpers/input.helper';
import { checkFileSize } from 'common/helpers/file';
import { IFile } from 'common/models';
import { showErrorMessage } from 'common/helpers/message.helper';
import { ReactComponent as AttachIcon } from 'app/assets/images/redesign/attach.svg';
import { ReactComponent as SendIcon } from 'app/assets/images/redesign/send.svg';
import { ReactComponent as CloseGreyIcon } from 'app/assets/images/redesign/close-grey.svg';

interface IComponentProps {
  token?: string;
  loading: boolean;
  onSend: (comment: string, files: string[], onSuccess: () => void) => void;
  setCommentBlockHeightPx: (value: number) => void;
}

export const RequestHistoryComment: FC<IComponentProps> = ({ token, loading, onSend, setCommentBlockHeightPx }) => {
  const [comment, setComment] = useState<string>('');
  const [fileList, setFileList] = useState<IFile[]>([]);
  const [filesLoading, setFilesLoading] = useState<boolean>(false);
  const ref = useRef<HTMLDivElement>(null);

  const calculateBlockHeight = () => {
    if (!ref.current) {
      return;
    }

    const rect = ref.current.getBoundingClientRect();
    const computed = window.getComputedStyle(ref.current);
    const marginTop = parseFloat(computed.marginTop) || 0;
    const marginBottom = parseFloat(computed.marginBottom) || 0;
    const totalHeight = rect.height + marginTop + marginBottom;

    setCommentBlockHeightPx(Math.round(totalHeight));
  };

  const handleFileUpload = (info: UploadChangeParam<UploadFile<{ id: string }>>) => {
    const { fileList, file } = info;

    if (fileList.some((item) => item.status === EFileStatus.Uploading)) {
      setFilesLoading(true);
    } else {
      setFilesLoading(false);
    }

    if (file.status === EFileStatus.Error) {
      showErrorMessage('Ошибка загрузки файла');
    }

    if (file.status === EFileStatus.Done && file.response?.id) {
      setFileList((prev) => [
        ...prev,
        {
          id: file.response?.id,
          uid: file.uid,
          name: file.name,
          status: file.status,
        },
      ]);
    }
  };

  const onSendClick = () => {
    onSend(
      comment,
      fileList.map((file) => file.id as string),
      () => {
        setComment('');
        setFileList([]);
      },
    );
  };

  useEffect(() => {
    calculateBlockHeight();

    const resizeObserver = new ResizeObserver(() => {
      calculateBlockHeight();
    });

    if (ref.current) {
      resizeObserver.observe(ref.current);
    }

    window.addEventListener('resize', calculateBlockHeight);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', calculateBlockHeight);
    };
  }, []);

  return (
    <div ref={ref} className="request-history-comment">
      {!!fileList.length && (
        <div className="request-history-comment__file-list">
          {fileList.map((file) => {
            return (
              <div key={file.uid} className={`request-history-comment__file-list-item ${file.status}`}>
                <div className="text-controls-accent ellipsis">{file.name}</div>

                {file.status !== EFileStatus.Uploading && (
                  <Button
                    className="button-icon"
                    icon={
                      <CloseGreyIcon className={file.status === EFileStatus.Error ? 'icon-close-red' : 'icon-close-dark-grey'} />
                    }
                    onClick={() => setFileList((prev) => prev.filter((item) => item.uid !== file.uid))}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="request-history-comment__container">
        <div className="request-history-comment__field">
          <Input.TextArea
            className="autosize-input"
            autoSize
            maxLength={255}
            allowClear={{ clearIcon: <span /> }}
            styles={{ textarea: { maxHeight: 90 } }}
            placeholder="Добавьте комментарий к заказу"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            onKeyDown={(e) => onInputKeyDown(e, onSendClick)}
            disabled={loading}
          />

          <Upload
            showUploadList={false}
            multiple={true}
            action="/api/files/upload"
            headers={{ authorization: `Bearer ${token}` }}
            accept="application/pdf"
            beforeUpload={checkFileSize}
            onChange={handleFileUpload}
          >
            <Button className={fileList.length ? 'full' : ''} icon={<AttachIcon />} loading={filesLoading || loading} />
          </Upload>
        </div>

        <Button
          className="button-circle primary"
          icon={<SendIcon className="icon-send-white" />}
          loading={filesLoading || loading}
          disabled={!comment.trim().length && !fileList.length}
          onClick={onSendClick}
        />
      </div>
    </div>
  );
};
